import React from "react";
import PropTypes from "prop-types";
import toast, { Toaster } from "react-hot-toast";
import SuggestionInput from "shared/@forms/SuggestionInput";
import { FiShoppingCart } from "react-icons/fi";
import Button, { ButtonPurpose } from "shared/Button";
import { Modal } from "shared/Modal";
import SearchTips from "../../../Statistics/components/SearchTips";
import ProductCatalogBrowser from "../../../Store/components/ProductCatalog/ProductCatalogBrowser";
import {
  ALL_MEMBERSHIP_CODES,
  ALL_PIN_CODES,
} from "../../../Store/productListItem";
import CartItemActions from "../ProductCatalog/CartItemActions";
import AddToCartModal from "../ProductCatalog/AddToCartModal";
import { ibos as ibosApi, errorMessage } from "shared/api";
import { orderHistoryTips } from "../../searchTips";
import { useIboSearch } from "../../hooks/useIboSearch";
import useOrderTaxEstimate from "../../hooks/useOrderTaxEstimate";
import {
  usePersistedPersonId,
  getIboPeople,
} from "../../hooks/usePersistedPersonId";
import OrderCard from "../OrderCard";
import OrderOptionsCard from "../OrderOptionsCard";
import IBODetailsCard from "../IBODetailsCard";
import SelectAddressModal, { TEMP_ADDRESS_ID } from "../SelectAddressModal";
import PlaceOrderModal from "../PlaceOrderModal";
import IboHeader from "../IboHeader";
import PreviouslyViewed from "shared/PreviouslyViewed";
import {
  getPreviouslyViewed,
  recordView,
} from "shared/@utils/previouslyViewed";
import styles from "./CreateOrder.module.css";
import { ChevronLeft } from "react-ikonate";

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const CHARGE_CARD_HINT =
  "Payment integration is pending — the card on file is never charged from the admin.";
// The BFF create-order flow drops `options` on write (storeOrders.js
// normalizeCreatePayload), so these toggles aren't persisted with the order yet.
const OPTION_NOT_PERSISTED_HINT =
  "Not saved with the order yet — backend support is pending.";

// Membership / pin-level are inclusion filters that default to all-on, so the
// catalog shows everything until the rep narrows it down. Status starts on
// Published: an order is for something members can buy. The rep can still tick
// other statuses.
const CART_INITIAL_FILTERS = {
  status: ["Published"],
  membership: ALL_MEMBERSHIP_CODES,
  pinLevel: ALL_PIN_CODES,
};

const subtotalOf = (items) =>
  items.reduce((sum, item) => {
    const price = Number(item.price ?? item.retailPrice ?? item.pm ?? 0) || 0;
    return sum + price * (item.qty || 1);
  }, 0);

export function CreateOrder({
  selectedIbo,
  onSelectIbo,
  iboCart,
  orderCart,
  activeMode,
  onModeChange,
  iboQuery,
  onIboQueryChange,
  onOrderPlaced,
}) {
  const [isCartOpen, setIsCartOpen] = React.useState(false);
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = React.useState(false);
  const [isSubmittingToCart, setIsSubmittingToCart] = React.useState(false);
  const [isCancelOrderOpen, setIsCancelOrderOpen] = React.useState(false);
  const [isPlaceOrderOpen, setIsPlaceOrderOpen] = React.useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = React.useState(false);
  // Which of the IBO's addresses this order ships to. Defaulted from the BFF's
  // own pick further down, once the selected IBO's addresses are known.
  const [shipToAddressId, setShipToAddressId] = React.useState(null);
  // A one-off destination the rep typed in for this order only. It is never
  // written back to CRM — the admin app is not allowed to mutate the directory.
  const [tempAddress, setTempAddress] = React.useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = React.useState(false);

  // The BFF's own default pick, mirrored into a ref so the reset below can run
  // from callbacks declared before it is computed.
  const defaultShipToIdRef = React.useRef(null);

  // "Temporary" means *this* order. Once an order is placed or discarded the
  // cart empties, so the destination has to empty with it — otherwise the next
  // order for the same IBO silently inherits a one-off address the rep typed
  // for the previous one. Switching IBO is handled separately, further down.
  const resetShipTo = React.useCallback(() => {
    const next = defaultShipToIdRef.current;
    setShipToAddressId(next == null ? null : String(next));
    setTempAddress(null);
  }, []);

  const isIboMode = activeMode === "ibo_cart";

  const people = React.useMemo(() => getIboPeople(selectedIbo), [selectedIbo]);

  // Which person on a shared IBO account the rep is acting as. Persisted per
  // IBO so it survives tab/page changes and refreshes.
  const [selectedPersonId, setSelectedPersonId] =
    usePersistedPersonId(selectedIbo);

  // The selected person's own email (David Nelson's per-contact query),
  // falling back to the account's. Account-first showed IBO-A's email while
  // the rep was acting as IBO-B.
  const contactEmail = React.useMemo(() => {
    const person = (people || []).find(
      (p) => String(p?.id) === String(selectedPersonId)
    );
    return person?.email || selectedIbo?.email || "";
  }, [selectedIbo, people, selectedPersonId]);

  // And the name the card shows beside them, so it never pairs one person's
  // name with another's contact details.
  const contactName = React.useMemo(() => {
    const person = (people || []).find(
      (p) => String(p?.id) === String(selectedPersonId)
    );
    return person?.name || selectedIbo?.name || "";
  }, [selectedIbo, people, selectedPersonId]);

  // Same for phone: the selected person's, falling back to the account's.
  const contactPhone = React.useMemo(() => {
    const person = (people || []).find(
      (p) => String(p?.id) === String(selectedPersonId)
    );
    return person?.phone || selectedIbo?.phone || "";
  }, [selectedIbo, people, selectedPersonId]);

  const handleIboSelect = React.useCallback(
    (ibo) => {
      recordView("ibo_search", {
        id: ibo.iboNumber || "",
        name: ibo.name || "",
        sku: "",
      });
      onSelectIbo(ibo);
      setIsCartOpen(false);
    },
    [onSelectIbo]
  );

  const handleIboSearchReset = React.useCallback(() => {
    onSelectIbo(null);
    setIsCartOpen(false);
  }, [onSelectIbo]);

  const handleClearIbo = React.useCallback(() => {
    handleIboSearchReset();
    onIboQueryChange("");
  }, [handleIboSearchReset, onIboQueryChange]);

  const {
    query,
    suggestions,
    isLoading,
    totalCount: iboTotalCount,
    hasMore: hasMoreIbos,
    isLoadingMore: isLoadingMoreIbos,
    loadMore: loadMoreIbos,
    fetchSuggestions,
    handleQueryChange,
    handleSelectIbo,
  } = useIboSearch({
    searchFn: ibosApi.search,
    onSelect: handleIboSelect,
    onQueryReset: handleIboSearchReset,
    query: iboQuery,
    setQuery: onIboQueryChange,
  });

  // Collapse the cart view whenever the selected IBO or destination changes.
  React.useEffect(() => {
    setIsCartOpen(false);
  }, [selectedIbo, activeMode]);

  const handleAddToCart = React.useCallback(
    (product, quantity = 1, selection) => {
      if (isIboMode) {
        iboCart.addToCart(product, quantity, selection);
      } else {
        orderCart.addToCart(product, quantity, selection);
      }
    },
    [isIboMode, iboCart, orderCart]
  );

  // ----- IBO cart (push to the IBO's own cart) -----------------------------
  const handleUpdateIboCartQty = React.useCallback(
    (lineId, quantity) => iboCart.updateCartQty(lineId, quantity),
    [iboCart]
  );

  const handleRemoveFromIboCart = React.useCallback(
    (lineIds) => iboCart.removeFromCart(lineIds),
    [iboCart]
  );

  const handleRequestSubmitToCart = React.useCallback(() => {
    setIsSubmitConfirmOpen(true);
  }, []);

  const handleConfirmSubmitToCart = React.useCallback(async () => {
    setIsSubmitConfirmOpen(false);
    if (isSubmittingToCart) return;

    const target = selectedIbo?.name || "the IBO";
    setIsSubmittingToCart(true);
    try {
      const result = await iboCart.submitPending(selectedPersonId);
      const attempted = result.attempted ?? result.committed ?? 0;
      if (!attempted) return;

      const label = `${attempted} item${attempted === 1 ? "" : "s"}`;
      if (result.simulated) {
        toast(
          `Nothing was written — the IBO cart service isn't configured yet. ${label} left pending.`,
          { icon: "⚠️" }
        );
        return;
      }
      const skipped = result.skipped || [];
      if (skipped.length) {
        toast(
          `Added ${result.committed} of ${label} to ${target}'s cart. ` +
            `${skipped.length} left pending: ${
              skipped[0].reason || "unknown reason"
            }`,
          { icon: "⚠️" }
        );
        return;
      }
      toast.success(`Added ${label} to ${target}'s cart.`);
    } catch (err) {
      toast.error(
        errorMessage(err, `Couldn't add the items to ${target}'s cart.`)
      );
    } finally {
      setIsSubmittingToCart(false);
    }
  }, [iboCart, isSubmittingToCart, selectedIbo, selectedPersonId]);

  // ----- Admin New Order cart ----------------------------------------------
  const handleUpdateOrderQty = React.useCallback(
    (lineId, quantity) => orderCart.updateQty(lineId, quantity),
    [orderCart]
  );

  const handleRemoveFromOrder = React.useCallback(
    (lineIds) => orderCart.remove(lineIds),
    [orderCart]
  );

  // `isPlacingOrder` drives the button's disabled state, but it can't be the
  // thing that guards the write: it is read from the render closure, so two
  // clicks landing before React commits the re-render would both see `false`
  // and place the order twice. A ref flips synchronously, so the second call
  // returns before it can reach `placeOrder`.
  const isPlacingRef = React.useRef(false);

  const handlePlaceOrder = React.useCallback(async () => {
    if (!orderCart.items.length || isPlacingRef.current) return;
    isPlacingRef.current = true;
    setIsPlacingOrder(true);
    try {
      await orderCart.placeOrder({
        ibo: selectedIbo,
        personId: selectedPersonId,
        shipToAddressId,
        // Only sent when the rep deliberately entered one. The BFF re-validates
        // it and ignores it unless `shipToAddressId` says to use it.
        tempAddress,
      });
      toast.success(`New order created for ${selectedIbo?.name || "the IBO"}.`);
      setIsPlaceOrderOpen(false);
      setIsCartOpen(false);
      resetShipTo();
      if (onOrderPlaced) onOrderPlaced();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't create the order."));
    } finally {
      isPlacingRef.current = false;
      setIsPlacingOrder(false);
    }
  }, [
    orderCart,
    selectedIbo,
    selectedPersonId,
    shipToAddressId,
    tempAddress,
    resetShipTo,
    onOrderPlaced,
  ]);

  const handleConfirmCancelOrder = React.useCallback(() => {
    orderCart.clear();
    setIsCancelOrderOpen(false);
    setIsCartOpen(false);
    resetShipTo();
    toast.success("Order discarded.");
  }, [orderCart, resetShipTo]);

  // ----- Shared catalog ----------------------------------------------------
  const activeCount = isIboMode ? iboCart.cartCount : orderCart.cartCount;

  // Editing a cart line re-opens the same colour/size/quantity modal the line
  // was created with, seeded with its current selection. It used to open the
  // Store product *editor* in a new tab, which is a different job entirely —
  // that edits the catalog product for everyone, not this one order line.
  const [editingLine, setEditingLine] = React.useState(null);

  // A line is only editable if the modal actually has something to offer for
  // it. Re-Ship seeds the order cart straight from order-history line items,
  // which carry sku/size/variationId but NO `variations` matrix — the modal
  // would then render a bare quantity stepper and confirm an empty selection,
  // wiping the line's colour, size and variationId on the way back in. The
  // quantity stepper on the row already covers quantity-only changes.
  const canEditLine = React.useCallback(
    (item) =>
      Boolean(item?.isBundle) ||
      (Array.isArray(item?.variations) && item.variations.length > 0),
    []
  );

  // `defaults` is a dependency of the modal's reset effect, so it has to be a
  // stable reference — an inline object would re-run that effect on every
  // render and stomp the rep's selection as they made it.
  const editingDefaults = React.useMemo(
    () =>
      editingLine
        ? {
            color: editingLine.selectedColor ?? editingLine.variationValue,
            size: editingLine.selectedSize ?? editingLine.size,
            qty: editingLine.qty || 1,
          }
        : undefined,
    [editingLine]
  );

  const handleConfirmEditLine = React.useCallback(
    (selection, quantity) => {
      if (!editingLine) return;
      // `cartKind` is routing state this component added, not part of the
      // product — keep it out of the line we write back.
      const { cartKind, ...product } = editingLine;
      // The modal derives `wasOverridden` from its own permissionMessage prop,
      // which the edit instance doesn't get — so it always confirms `false`.
      // Carry the line's existing flag forward, otherwise the tier-permission
      // audit badge silently disappears the first time a rep changes a size on
      // a restricted product.
      const nextSelection = {
        ...selection,
        wasOverridden: selection?.wasOverridden || editingLine.wasOverridden,
      };
      // The line id is a signature of product + colour + size, so a changed
      // selection is a different line. Drop the old one and add the new
      // selection, which also merges correctly if the cart already holds a
      // line matching the new choice.
      if (cartKind === "ibo") {
        iboCart.removeFromCart([editingLine.lineId]);
        iboCart.addToCart(product, quantity, nextSelection);
      } else {
        orderCart.remove([editingLine.lineId]);
        orderCart.addToCart(product, quantity, nextSelection);
      }
      setEditingLine(null);
    },
    [editingLine, iboCart, orderCart]
  );

  const handleViewCartClick = React.useCallback(() => {
    setIsCartOpen((prev) => !prev);
  }, []);

  const renderItemActions = React.useCallback(
    (product) => (
      <CartItemActions
        product={product}
        onAddToCart={handleAddToCart}
        iboPinCode={selectedIbo?.pinLevel || "IBO"}
        iboMembershipCode={selectedIbo?.membership || "C"}
      />
    ),
    [handleAddToCart, selectedIbo]
  );

  const renderSuggestion = React.useCallback(
    (ibo, closePanel) => (
      <button
        key={ibo.iboNumber}
        type="button"
        title={`Select ${ibo.name} (IBO: ${ibo.iboNumber}) to start an order.`}
        className={styles.suggestionRow}
        onClick={() => {
          handleSelectIbo(ibo);
          closePanel();
        }}
      >
        <span className={styles.suggestionLabel}>IBO:</span>
        <span className={styles.suggestionPill}>
          {ibo.searchLabel || ibo.name} (IBO: {ibo.iboNumber})
        </span>
      </button>
    ),
    [handleSelectIbo]
  );

  // ----- Derived cart data for the active destination ----------------------
  // The IBO's cart is taxed by the storefront at their checkout, so the admin
  // shows no tax for it.
  const iboCartItems = iboCart.cartItems;
  const iboSubtotal = subtotalOf(iboCartItems);
  const iboTotal = iboSubtotal;

  // A New Order is priced and taxed by the BFF when it's placed; the estimate
  // is that same calculation run early. Until it answers (or when it can't
  // work tax out) the cart's own subtotal stands and tax reads "—".
  const orderItems = orderCart.items;
  const taxEstimate = useOrderTaxEstimate({
    items: orderItems,
    iboId: selectedIbo?.id ?? selectedIbo?.iboNumber ?? null,
    shipToAddressId,
    tempAddress: shipToAddressId === TEMP_ADDRESS_ID ? tempAddress : null,
  });
  const hasTax = taxEstimate && taxEstimate.taxCents != null;
  const orderSubtotal =
    taxEstimate && taxEstimate.subtotalCents != null
      ? taxEstimate.subtotalCents / 100
      : subtotalOf(orderItems);
  const orderTax = hasTax ? money(taxEstimate.taxCents / 100) : "—";
  const orderTotal = hasTax ? taxEstimate.totalCents / 100 : orderSubtotal;

  // Kevin Shay (2026-09-14): the Primary address is the billing address, and it
  // is snapshotted onto the transaction. That is deliberately *not* the same as
  // `selectedIbo.address`, which is the shipping address (Default Shipping when
  // one is set), so the two blocks can legitimately differ.
  const billingAddress = React.useMemo(() => {
    const primary = (selectedIbo?.addresses || []).find((a) => a.isPrimary);
    return primary?.formatted || null;
  }, [selectedIbo]);

  // The order ships to the IBO's Default Shipping address (falling back to
  // Primary) unless the rep picks another one on file — `shippingAddressId` is
  // the BFF's own pick, so the default here always matches what the IBO
  // Details card is showing.
  const shippingAddresses = React.useMemo(
    () => selectedIbo?.addresses || [],
    [selectedIbo]
  );

  // Re-default whenever the rep switches IBO, or when the addresses arrive
  // after the initial render. Keyed on the id so a re-fetch returning the same
  // data doesn't clobber a choice the rep already made.
  const defaultShipToId = selectedIbo?.shippingAddressId ?? null;
  const shipToKey = `${selectedIbo?.id ?? selectedIbo?.iboNumber ?? ""}:${
    defaultShipToId ?? ""
  }`;
  const lastShipToKey = React.useRef(null);
  React.useEffect(() => {
    defaultShipToIdRef.current = defaultShipToId;
    if (lastShipToKey.current === shipToKey) return;
    lastShipToKey.current = shipToKey;
    setShipToAddressId(
      defaultShipToId == null ? null : String(defaultShipToId)
    );
    // A temporary address belongs to the IBO it was typed for. Switching IBOs
    // must not carry someone else's one-off destination across.
    setTempAddress(null);
  }, [shipToKey, defaultShipToId]);

  // What the cart is actually shipping to, resolved once and reused by the IBO
  // Details card, the Place Order modal and the create-order payload so the
  // three can't disagree. Falls back to `selectedIbo.address` (the BFF's own
  // pick) while the addresses list is still loading.
  const isTempShipTo =
    Boolean(tempAddress) && shipToAddressId === TEMP_ADDRESS_ID;
  const shipToAddress = React.useMemo(() => {
    if (isTempShipTo) return tempAddress.formatted || "";
    const chosen = shippingAddresses.find(
      (a) => String(a.id) === String(shipToAddressId)
    );
    return chosen?.formatted || selectedIbo?.address || "";
  }, [
    isTempShipTo,
    tempAddress,
    shippingAddresses,
    shipToAddressId,
    selectedIbo,
  ]);

  const handleSaveAddress = React.useCallback((next) => {
    setShipToAddressId(next.shipToAddressId);
    setTempAddress(next.tempAddress);
  }, []);

  const placeOrderItems = React.useMemo(
    () =>
      orderItems.map((item) => ({
        id: item.lineId,
        image: item.image,
        name: item.name,
        qty: item.qty || 1,
        price: money(item.price ?? item.retailPrice ?? item.pm ?? 0),
      })),
    [orderItems]
  );

  return (
    <div className={styles.createOrder}>
      <div className={styles.toolbar}>
        <div className={styles.searchLabel}>
          Search for IBOs by Name or IBO Number
        </div>
        <div className={styles.searchInput}>
          <SuggestionInput
            type="search"
            placeholder="Search IBOs"
            aria-label="Search IBOs"
            value={query}
            onChange={handleQueryChange}
            fetchSuggestions={fetchSuggestions}
            suggestions={suggestions}
            isLoading={isLoading}
            totalCount={iboTotalCount}
            hasMore={hasMoreIbos}
            isLoadingMore={isLoadingMoreIbos}
            onLoadMore={loadMoreIbos}
            minLength={1}
          >
            {renderSuggestion}
          </SuggestionInput>
        </div>
      </div>

      {!selectedIbo ? (
        <>
          <section className={styles.emptyState}>
            <h2 className={styles.emptyTitle}>No IBO selected</h2>
            <p className={styles.emptySubtitle}>
              But you can use the search box above to find one.
            </p>
          </section>

          <PreviouslyViewed
            items={getPreviouslyViewed("ibo_search")}
            onSelect={(item) => {
              fetchSuggestions(item.name);
            }}
          />

          <SearchTips tips={orderHistoryTips} />
        </>
      ) : (
        <div className={styles.selection}>
          <IboHeader
            selectedIbo={selectedIbo}
            people={people}
            selectedPersonId={selectedPersonId}
            onPersonChange={setSelectedPersonId}
            onClear={handleClearIbo}
          />

          {/* Two destinations for the same catalog (Figma): a brand-new admin
              order vs. the IBO's own cart. These are genuinely separate carts. */}
          <div className={styles.destinationBar} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={!isIboMode}
              title="Switch to building a brand-new admin order for this IBO."
              className={`${styles.destinationButton} ${
                !isIboMode ? styles.destinationButtonActive : ""
              }`}
              onClick={() => onModeChange("new_order")}
            >
              Create New Order
              {orderCart.cartCount > 0 ? ` (${orderCart.cartCount})` : ""}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isIboMode}
              title="Switch to adding items to the IBO's own cart."
              className={`${styles.destinationButton} ${
                isIboMode ? styles.destinationButtonActive : ""
              }`}
              onClick={() => onModeChange("ibo_cart")}
            >
              Add To IBOs Cart
              {iboCart.cartCount > 0 ? ` (${iboCart.cartCount})` : ""}
            </button>
          </div>

          {/* The catalog stays mounted (hidden behind the cart) so its search /
              filter / sort / pagination state survives toggling the cart. */}
          <div style={{ display: isCartOpen ? "none" : "block" }}>
            <ProductCatalogBrowser
              key={selectedIbo.iboNumber}
              actionColWidth="180px"
              searchPlaceholder="Search Store"
              initialFilters={CART_INITIAL_FILTERS}
              renderItemActions={renderItemActions}
              toolbarActions={
                <button
                  type="button"
                  className={styles.viewCartButton}
                  title={
                    isIboMode
                      ? "View the items in the IBO's cart."
                      : "View the items in this order's cart."
                  }
                  onClick={handleViewCartClick}
                  disabled={!activeCount}
                >
                  <FiShoppingCart size={16} />
                  <span>
                    {isIboMode ? "View IBOs Cart" : "View Cart"} ({activeCount})
                  </span>
                </button>
              }
            />
          </div>

          {isCartOpen && isIboMode && (
            <div className={styles.cartPanel}>
              <div className={styles.cartTopBar}>
                <button
                  type="button"
                  className={styles.continueShopping}
                  title="Return to the product catalog to keep adding items."
                  onClick={handleViewCartClick}
                >
                  <ChevronLeft size={18} />
                  Continue Shopping
                </button>
              </div>
              {iboCartItems.length ? (
                <div className={styles.cartLayout}>
                  {/* No address pencil here: items submitted to the IBO's own
                      cart are checked out by the IBO, who picks their own
                      destination. Only the admin-placed order has a ship-to. */}
                  <IBODetailsCard
                    name={contactName}
                    address={selectedIbo.address}
                    phoneNum={contactPhone}
                    email={contactEmail}
                    subTotal={money(iboSubtotal)}
                    tax="—"
                    total={money(iboTotal)}
                  />
                  <div className={styles.cartMain}>
                    <OrderCard
                      selectable
                      onRemoveSelected={handleRemoveFromIboCart}
                      items={iboCartItems.map((item) => ({
                        id: item.lineId,
                        image: item.image,
                        name: item.name,
                        date: item.date,
                        retailPrice: item.retailPrice,
                        pm: item.pm,
                        variation: item.selectedColor ?? item.variationValue,
                        variationLabel: item.selectedColor
                          ? "Color"
                          : item.variationLabel,
                        size: item.selectedSize ?? item.size,
                        sku: item.sku,
                        price: item.price ?? item.retailPrice ?? item.pm ?? "",
                        qty: item.qty || 1,
                        addedByAdmin: item.addedByAdmin,
                        wasOverridden: item.wasOverridden,
                        // The IBO's own items are read-only to the admin, but
                        // anything the admin added can be removed even after
                        // it's been submitted. Quantity stays editable only
                        // while the line is still a pending draft.
                        pending: item.pending,
                        selectable: item.addedByAdmin,
                        onQtyChange: item.pending
                          ? (value) =>
                              handleUpdateIboCartQty(item.lineId, value)
                          : undefined,
                        showButtons: true,
                        // Only a line the admin added and hasn't submitted yet
                        // can be re-specced here. The IBO's own lines aren't
                        // ours to change, and a committed line already lives in
                        // the member's cart.
                        secondaryBtnText:
                          item.addedByAdmin && item.pending && canEditLine(item)
                            ? "Edit"
                            : null,
                        onSecondaryClick: () =>
                          setEditingLine({ ...item, cartKind: "ibo" }),
                      }))}
                    />
                  </div>
                </div>
              ) : (
                <p className={styles.cartEmpty}>Your cart is empty.</p>
              )}
              {iboCartItems.length ? (
                <div className={styles.cartFooter}>
                  {/* The IBO-cart push runs end-to-end: the BFF resolves the
                      NetSuite SKU + colour/size to SQL ids, resolves the person
                      to a CRM contact id, and calls func-bsm-api's
                      cart/{iboNumber}/items. Where the gateway settings are
                      absent the BFF answers with a typed 503 that surfaces here
                      as a toast — never as a fake success. */}
                  {iboCart.pendingCount > 0 && (
                    <div className={styles.cartSubmit}>
                      <Button
                        type="button"
                        purpose={ButtonPurpose.save}
                        onClick={handleRequestSubmitToCart}
                        disabled={isSubmittingToCart}
                      >
                        {isSubmittingToCart
                          ? "Submitting…"
                          : `Submit ${iboCart.pendingCount} item${
                              iboCart.pendingCount === 1 ? "" : "s"
                            } to Cart`}
                      </Button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {isCartOpen && !isIboMode && (
            <div className={styles.cartPanel}>
              <div className={styles.cartTopBar}>
                <button
                  type="button"
                  className={styles.continueShopping}
                  title="Return to the product catalog to keep adding items."
                  onClick={handleViewCartClick}
                >
                  <ChevronLeft size={18} />
                  Continue Shopping
                </button>
              </div>
              {/* Checkout / Cancel sit at the top-left (per scope) so they stay
                  reachable without scrolling past a long list of items. */}
              {orderItems.length ? (
                <div className={styles.cartActions}>
                  <Button
                    type="button"
                    purpose={ButtonPurpose.save}
                    title="Review this order before placing it."
                    onClick={() => setIsPlaceOrderOpen(true)}
                    disabled={isPlacingOrder}
                  >
                    {isPlacingOrder ? "Placing Order…" : "Checkout"}
                  </Button>
                  <Button
                    type="button"
                    purpose={ButtonPurpose.delete}
                    title="Discard this new order and clear its items and options."
                    onClick={() => setIsCancelOrderOpen(true)}
                  >
                    Cancel Order
                  </Button>
                </div>
              ) : null}
              {orderItems.length ? (
                <div className={styles.cartLayout}>
                  <div className={styles.orderSidebar}>
                    <IBODetailsCard
                      name={contactName}
                      address={shipToAddress}
                      isTempAddress={isTempShipTo}
                      onEditAddress={() => setIsAddressModalOpen(true)}
                      phoneNum={contactPhone}
                      email={contactEmail}
                      subTotal={money(orderSubtotal)}
                      tax={orderTax}
                      total={money(orderTotal)}
                    />
                    <OrderOptionsCard
                      options={orderCart.options}
                      onChange={orderCart.setOption}
                      disabledKeys={["chargeCardOnFile"]}
                      hints={{
                        chargeCardOnFile: CHARGE_CARD_HINT,
                        markAsWWG: OPTION_NOT_PERSISTED_HINT,
                        priorityShipping: OPTION_NOT_PERSISTED_HINT,
                      }}
                    />
                  </div>
                  <div className={styles.cartMain}>
                    <OrderCard
                      selectable
                      onRemoveSelected={handleRemoveFromOrder}
                      items={orderItems.map((item) => ({
                        id: item.lineId,
                        image: item.image,
                        name: item.name,
                        date: item.date,
                        retailPrice: item.retailPrice,
                        pm: item.pm,
                        variation: item.selectedColor ?? item.variationValue,
                        variationLabel: item.selectedColor
                          ? "Color"
                          : item.variationLabel,
                        size: item.selectedSize ?? item.size,
                        sku: item.sku,
                        price: item.price ?? item.retailPrice ?? item.pm ?? "",
                        qty: item.qty || 1,
                        wasOverridden: item.wasOverridden,
                        selectable: true,
                        onQtyChange: (value) =>
                          handleUpdateOrderQty(item.lineId, value),
                        showButtons: true,
                        secondaryBtnText: canEditLine(item) ? "Edit" : null,
                        onSecondaryClick: () =>
                          setEditingLine({ ...item, cartKind: "order" }),
                      }))}
                    />
                  </div>
                </div>
              ) : (
                <p className={styles.cartEmpty}>Your cart is empty.</p>
              )}
            </div>
          )}
        </div>
      )}
      <AddToCartModal
        product={editingLine || {}}
        isOpen={Boolean(editingLine)}
        defaults={editingDefaults}
        isEdit
        onClose={() => setEditingLine(null)}
        onConfirm={handleConfirmEditLine}
      />
      <PlaceOrderModal
        isOpen={isPlaceOrderOpen}
        onClose={() => setIsPlaceOrderOpen(false)}
        onConfirm={handlePlaceOrder}
        isPlacing={isPlacingOrder}
        items={placeOrderItems}
        subTotal={money(orderSubtotal)}
        tax={orderTax}
        total={money(orderTotal)}
        billingAddress={billingAddress}
        shipToAddress={shipToAddress}
        isTempShipTo={isTempShipTo}
      />
      <SelectAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onSave={handleSaveAddress}
        addresses={shippingAddresses}
        shipToAddressId={shipToAddressId}
        tempAddress={tempAddress}
      />
      <Modal
        title="Submit items to cart?"
        isOpen={isSubmitConfirmOpen}
        onClose={() => setIsSubmitConfirmOpen(false)}
        className={styles.notifyModal}
      >
        <p className={styles.notifyIntro}>
          {`This will add ${iboCart.pendingCount} item${
            iboCart.pendingCount === 1 ? "" : "s"
          } to ${
            selectedIbo?.name || "the IBO"
          }'s cart. They'll see these items the next time they check out.`}
        </p>
        <div className={styles.notifyActions}>
          <Button
            type="button"
            purpose={ButtonPurpose.generic}
            title="Close without submitting items to the cart."
            onClick={() => setIsSubmitConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            purpose={ButtonPurpose.save}
            title="Add the pending items to the IBO's cart."
            onClick={handleConfirmSubmitToCart}
          >
            {`Submit ${iboCart.pendingCount} item${
              iboCart.pendingCount === 1 ? "" : "s"
            }`}
          </Button>
        </div>
      </Modal>
      <Modal
        title="Discard this order?"
        isOpen={isCancelOrderOpen}
        onClose={() => setIsCancelOrderOpen(false)}
        className={styles.notifyModal}
      >
        <p className={styles.notifyIntro}>
          This clears the items and options from the new order you're building.
          The IBO's own cart and any existing orders are not affected.
        </p>
        <div className={styles.notifyActions}>
          <Button
            type="button"
            purpose={ButtonPurpose.generic}
            title="Keep editing this order without discarding it."
            onClick={() => setIsCancelOrderOpen(false)}
          >
            Keep Editing
          </Button>
          <Button
            type="button"
            purpose={ButtonPurpose.delete}
            title="Discard this order and clear its items and options. The IBO's cart and existing orders are not affected."
            onClick={handleConfirmCancelOrder}
          >
            Discard Order
          </Button>
        </div>
      </Modal>
      <Toaster position="bottom-right" />
    </div>
  );
}

CreateOrder.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedIbo: PropTypes.object,
  onSelectIbo: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  iboCart: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  orderCart: PropTypes.object.isRequired,
  activeMode: PropTypes.oneOf(["new_order", "ibo_cart"]),
  onModeChange: PropTypes.func,
  iboQuery: PropTypes.string,
  onIboQueryChange: PropTypes.func,
  onOrderPlaced: PropTypes.func,
};

CreateOrder.defaultProps = {
  selectedIbo: null,
  activeMode: "new_order",
  onModeChange: () => {},
  iboQuery: undefined,
  onIboQueryChange: undefined,
};

export default CreateOrder;
